import { Point, mathUtils } from "@/lib/math";
import Victor from 'victor';
import { ToothLongAxisHelper } from "../ToothLongAxisHelper";

export class ToothLongAxisBuilder {

    static MAX_DISTANCE_DIFFERENCE_FROM_LONGEST_LINE = 40;

    /**
     * 
     * @param {Point[]} toothPoints 
     * @param {'superior' | 'inferior'} posicaoDente 
     */
    constructor(toothPoints, posicaoDente, toothId) {
        /**
         * @type {Point[]}
         */
        this.toothPoints = toothPoints;
        // console.log('ToothLongAxisBuilder.toothPoints', this.toothPoints)
        /**
         * @type {'superior' | 'inferior'}
         * @private
         * @readonly
         */
        this.posicaoDente = posicaoDente;

        this.toothId = toothId;
    }

    /**
     * 
     * @returns {[Point, Point]}
     */
    build() {
        try {
            const toothBbox = mathUtils.getBoundingRect(this.toothPoints)
            const length = Math.max(toothBbox.height, toothBbox.width)
            const resampledPoints = this._resamplePoints(this.toothPoints)
            const toothCentroid = this._getToothCentroid(resampledPoints)
            const toothAngle = this._getToothAngleRad(resampledPoints)
            // console.log(this.toothId, 'toothAngle', toothAngle, 'toothCentroid', toothCentroid, 'toothLength', length)
            const vCentroid = Victor.fromObject(toothCentroid)
            const sideLength = length * 0.4
            let longAxisSegment = [
                Victor(sideLength, 0).rotate(toothAngle).add(vCentroid).toObject(),
                Victor(-sideLength, 0).rotate(toothAngle).add(vCentroid).toObject()
            ]
            // console.log(this.toothId, 'longAxisSegment', longAxisSegment)
            longAxisSegment = ToothLongAxisHelper.orderLongAxisSegment(longAxisSegment, this.posicaoDente)
            longAxisSegment = mathUtils.extendLineSegment(longAxisSegment, 1.15, 'end')
            return longAxisSegment;
        } catch (error) {
            console.error('Falha na construcao do longo eixo', this.toothId, error,
                [...this.toothPoints]
            )
        }
        return [];
    }

    /**
     * Calcula o centroide do dente, o meio da massa.
     * @param {Array<Point>} points 
     * @returns 
     */
    _getToothCentroid(points) {
        let m00 = 0, m10 = 0, m01 = 0;
        const len = points.length;

        // Step 1: Calculate Spatial Moments to find Centroid (Center of Mass)
        for (let i = 0; i < len; i++) {
            m00 += 1; // Total point mass
            m10 += points[i].x;
            m01 += points[i].y;
        }

        const cx = m10 / m00; // Centroid X
        const cy = m01 / m00; // Centroid Y

        return {
            x: cx,
            y: cy
        }
    }

    /**
     * Diminui o numero de pontos repetidos (Remover o cerrilhado da segmentacao)
     * @param {Point[]} points
     */
    _resamplePoints(points, spacing = 5) {
        if (points.length < 2) return points;

        const resampled = [points[0]];
        let accumulatedDistance = 0;

        for (let i = 0; i < points.length - 1; i++) {
            const p1 = points[i];
            const p2 = points[i + 1];
            const dx = p2.x - p1.x;
            const dy = p2.y - p1.y;
            const segmentLength = Math.sqrt(dx * dx + dy * dy);

            if (segmentLength === 0) continue;

            let remainder = segmentLength;
            let currentPosition = 0;

            while (currentPosition + (spacing - accumulatedDistance) <= segmentLength) {
                const step = spacing - accumulatedDistance;
                currentPosition += step;
                const ratio = currentPosition / segmentLength;

                resampled.push({
                    x: p1.x + dx * ratio,
                    y: p1.y + dy * ratio
                });

                accumulatedDistance = 0;
            }
            accumulatedDistance += (segmentLength - currentPosition);
        }

        return resampled;
    }


    /**
     * Calculates the primary orientation angle of a dense tooth contour.
     * Handles single or split roots flawlessly.
     * @param {Array<Point>} points 
     * @returns {number} Angle in degrees (-90 to 90) relative to the horizontal axis.
     */
    _getToothAngleRad(points) {
        const len = points.length;

        const centroid = this._getToothCentroid(points)

        // Step 2: Calculate Central Moments (covariance of the shape)
        // mu20 - O quanto esta espalhado horizontalmente (ao quadrado)
        // mu02 - O quanto esta espalhado verticalmente (ao quadrado)
        // mu11 - Covariancia entre x e y, qual lado ele esta inclinando (negativo = esquerda, positivo = direita)
        let mu20 = 0, mu02 = 0, mu11 = 0;
        for (let i = 0; i < len; i++) {
            const dx = points[i].x - centroid.x;
            const dy = points[i].y - centroid.y;

            mu20 += dx * dx;
            mu02 += dy * dy;
            mu11 += dx * dy;
        }

        // Step 3: Calculate the angle of the principal axis
        // This is a closed-form formula for the major eigenvector
        const diff = mu20 - mu02;
        let angleRad = 0.5 * Math.atan2(2 * mu11, diff);

        return angleRad;
    }

}